import { Phone, Brain, BarChart3, Shield, Zap, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface TechnologySection {
  id: string;
  title: string;
  content: string;
}

export interface TechnologyFeature {
  icon: LucideIcon;
  title: string;
  description: string;
  tooltipContent: string;
  features: string[];
}

export const technologySections: TechnologySection[] = [
  {
    id: "how-it-works",
    title: "How It Works",
    content: `Your voice is more than words—it's a window into your health

Every time a patient calls your practice, our technology is quietly working in the background, analyzing speech patterns that could indicate early signs of cognitive or neurological changes. No special equipment. No extra appointments. Just breakthrough science integrated seamlessly into everyday conversations.`
  },
  {
    id: "journey",
    title: "The Journey from Call to Care",
    content: `1. Patient Makes Contact
A patient calls to schedule an appointment, check test results, or refill a prescription—just like they always have.

2. Voice Analysis Begins
Our AI captures and analyzes multiple dimensions of speech in real-time, including pace, tone, pauses, and articulation patterns.

3. Biomarker Detection
Clinically validated algorithms compare speech patterns against established baselines and population norms to identify potential changes.

4. Provider Insights
Healthcare providers receive actionable insights through their existing workflows, with risk indicators and longitudinal tracking.

5. Enhanced Care Decisions
Armed with objective data, providers can make more informed decisions about screening, interventions, and care planning.`
  }
];

export const technologyFeatures: TechnologyFeature[] = [
  {
    icon: Phone,
    title: "Natural Conversation Intelligence",
    description: "Our platform handles natural, free-flowing conversations without scripts or prompts. Patients interact normally while our technology works invisibly in the background.",
    tooltipContent: "Advanced voice AI that processes natural conversations in real-time without requiring special scripts or prompts from patients.",
    features: [
      "Real-time speech processing",
      "40+ language support",
      "Accent and dialect adaptation",
      "Interruption handling",
      "Context awareness",
      "Seamless integration with existing phone systems"
    ]
  },
  {
    icon: Brain,
    title: "Speech Biomarker Analysis",
    description: "Speech biomarkers are measurable characteristics of speech that correlate with specific health conditions. Like blood pressure or heart rate, these vocal features can indicate changes in neurological or cognitive health.",
    tooltipContent: "Clinically validated analysis of speech characteristics that can indicate neurological and cognitive health changes before other symptoms appear.",
    features: [
      "Temporal features: Speaking rate, pause patterns, response latency",
      "Acoustic properties: Pitch variability, voice quality, articulation precision",
      "Linguistic patterns: Word choice, sentence complexity, semantic fluency",
      "Conversational dynamics: Turn-taking, topic maintenance, coherence"
    ]
  },
  {
    icon: BarChart3,
    title: "Intelligence Engine",
    description: "Our system improves with every interaction, creating personalized baselines for each patient and enabling detection of subtle changes over time that might indicate health changes.",
    tooltipContent: "Machine learning engine that establishes personalized baselines and continuously learns to detect subtle health changes over time.",
    features: [
      "Continuous learning from longitudinal patient data",
      "Clinical outcomes integration",
      "Provider feedback incorporation",
      "Research updates integration",
      "Baseline establishment",
      "Risk stratification by urgency and confidence"
    ]
  },
  {
    icon: Shield,
    title: "Security & Privacy",
    description: "Your trust is our foundation. Full HIPAA compliance with enterprise-grade security measures to protect patient data and maintain privacy.",
    tooltipContent: "Enterprise-grade security with HIPAA compliance, ensuring patient data is protected with the highest standards of privacy and security.",
    features: [
      "Full HIPAA compliance with BAA",
      "SOC 2 Type II certified",
      "Real-time analysis with immediate data purging",
      "No recording storage",
      "De-identification protocols",
      "Role-based access controls and audit trails"
    ]
  },
  {
    icon: Zap,
    title: "Integration & Implementation",
    description: "Built for healthcare reality with minimal IT footprint. Cloud-based deployment with no on-premise servers required, compatible with all major phone systems.",
    tooltipContent: "Seamless integration with existing healthcare systems and workflows with minimal IT requirements and comprehensive support.",
    features: [
      "Minimal IT footprint",
      "No on-premise servers required",
      "Compatible with VoIP and traditional phone lines",
      "EHR system integration",
      "24/7 technical support",
      "Comprehensive training materials"
    ]
  },
  {
    icon: Users,
    title: "Clinical Validation",
    description: "Our biomarkers are based on peer-reviewed research and validated through clinical studies. We continuously refine our algorithms based on the latest scientific evidence.",
    tooltipContent: "Research-backed technology validated through rigorous clinical studies with continuous improvement based on latest scientific evidence.",
    features: [
      "Multi-site clinical studies",
      "Diverse population testing",
      "Longitudinal outcome tracking",
      "Peer review publication",
      "Regular algorithm updates",
      "Enhanced accuracy metrics"
    ]
  }
];